import { Link } from "react-router-dom"
import { Card, CardFooter, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { formatMXN } from "@/content/prices"
import { cn } from "@/lib/utils"

const TEXT_GRADIENT_CLASS = "bg-[linear-gradient(to_top,rgba(255,255,255,0.8)_15%,transparent_50%)]"

export interface CatalogCardPrice {
	hasDiscount: boolean
	original: number
	final: number
	discountLabel?: string
}

interface CatalogCardProps {
	href: string
	imageSrc: string
	eager?: boolean
	title: string
	subtitle: string
	price: CatalogCardPrice
	hasFreeShipping: boolean
	buyButton: React.ReactNode
}

/** Tarjeta con imagen de fondo reutilizada por /productos, /tratamientos y /servicios. */
export default function CatalogCard({
	href,
	imageSrc,
	eager = false,
	title,
	subtitle,
	price,
	hasFreeShipping,
	buyButton
}: CatalogCardProps): React.JSX.Element {
	return (
		<Card className="relative flex flex-col gap-0 overflow-hidden p-0 py-0">
			{hasFreeShipping && <Badge className="absolute top-4 left-4 z-20 bg-sky-500/20 text-sky-700">envío gratuito</Badge>}
			{price.hasDiscount && (
				<Badge variant="destructive" className="absolute top-4 right-4 z-20">
					-{price.discountLabel}
				</Badge>
			)}
			<Link to={href} className="group relative flex flex-1 cursor-pointer flex-col">
				<div aria-hidden className="absolute inset-0 overflow-hidden">
					<img
						src={imageSrc}
						alt=""
						className="h-full w-full object-cover opacity-60 transition-opacity duration-300 group-hover:opacity-100"
						loading={eager ? "eager" : "lazy"}
					/>
				</div>
				<div aria-hidden className={cn("absolute inset-0", TEXT_GRADIENT_CLASS)} />
				<div className="relative z-10 px-4 pb-4 pt-25 sm:pt-37.5">
					<div className="flex items-start justify-between gap-3">
						<CardTitle className="text-base">{title}</CardTitle>
						<div className="flex shrink-0 flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0.5">
							{price.hasDiscount && (
								<span className="text-sm text-muted-foreground line-through">{formatMXN(price.original)}</span>
							)}
							<span className="text-base font-medium text-primary">{formatMXN(price.final)}</span>
						</div>
					</div>
					<p className="text-sm text-muted-foreground">{subtitle}</p>
				</div>
			</Link>

			<CardFooter className="justify-between gap-2 border-t px-4 py-3 [--card-spacing:--spacing(4)] [.border-t]:pt-3">
				<Button asChild variant="secondary" size="sm" className="shrink-0">
					<Link to={href}>Ver detalles</Link>
				</Button>
				{buyButton}
			</CardFooter>
		</Card>
	)
}
